/**
 * Reads the server's verdict on whether the tenant may see a "you're done" screen.
 *
 * WHY THIS EXISTS
 * ---------------
 * Congratulations used to be gated on `journey.steps[BANKING].status === 'SUCCESS'`. That
 * field is written by 13 different places in cpcore, none of which check that the webhook they
 * acted on belongs to the tenant's CURRENT banking attempt. A late approval for attempt #1
 * therefore set it to SUCCESS while attempt #3 had already failed — and the tenant saw
 * CONGRATULATIONS. Reproduced in production on journey CPJ2608030642XC7B1.
 *
 * cpcore now re-derives the answer at read time from the step AND the attempt the step points
 * at, and publishes it as `gates` on GET /journey/full. This module is the only place the B2B
 * app reads it.
 *
 * ⚠️ THIS FILE LIVES UNDER src/b2b/ ON PURPOSE.
 * B2B and D2C deliberately share ZERO code — D2C is slated for deletion, and anything shared
 * would make it undeletable. Do not move this to a common folder, and do not import it from
 * src/components/journey/** (the D2C tree). If D2C ever needs the same idea, duplicate it.
 */

import { fetchData } from "@/lib/helper";

/** What a screen should do when it cannot get a verdict. */
export const GATE_DECISION = {
  ALLOW: "ALLOW",
  REDIRECT: "REDIRECT",
};

/**
 * Fetch the journey plus its gates.
 *
 * Returns `{ journey, gates }`. `gates` is `null` when the backend has not shipped the field
 * yet — that is a NORMAL state, not an error: cpcore and tenant_journey deploy independently,
 * so the frontend must work against a cpcore that predates this feature.
 */
export async function fetchJourneyWithGates() {
  const response = await fetchData("journey/full");
  return {
    journey: response?.journey ?? null,
    gates: response?.gates ?? null,
  };
}

/**
 * Decide whether a guarded destination may render.
 *
 * The three cases, and why each behaves the way it does:
 *
 *  1. `gates` is MISSING → **allow**. The backend is older than this feature. Blocking here
 *     would break every tenant the moment the frontend deployed ahead of cpcore, which is the
 *     normal deploy order for an additive API field.
 *
 *  2. `gates.allowed[screen]` is `false` → **redirect**. cpcore has actively decided this
 *     tenant may not see the screen. Note cpcore already accounts for its own enforcement
 *     mode: while the gate is `off`/`report` it publishes the verdict but keeps `allowed`
 *     true, so this branch only fires once ops switches the gate to `apply`. The client never
 *     enforces something the server has not switched on.
 *
 *  3. anything else → **allow**.
 *
 * @param gates  the `gates` block from /journey/full, or null
 * @param screen "congrats" | "postBanking" | "happyMoveIn"
 *
 *   `postBanking` guards every screen that PRESUMES banking is done — the onboarding step list
 *   and the rent summary. It exists because gating Congratulations alone only moves the leak:
 *   `bank_details` and `HomeScreen` forward on the stale `Lead.bank_eligible` mirror, so a
 *   tenant correctly refused Congratulations was still waved onto a step list that reported
 *   "Income Verification — Completed".
 */
export function evaluateGate(gates, screen) {
  if (!gates || !gates.allowed) {
    return { decision: GATE_DECISION.ALLOW, reason: "GATES_UNAVAILABLE" };
  }

  if (gates.allowed[screen] === false) {
    return {
      decision: GATE_DECISION.REDIRECT,
      // `fallbackScreen` is chosen server-side from the tenant's real state — a banking step
      // parked in lender review sends them to banking-review, a retryable failure to banking.
      // It is deliberately never "banking-limit": that screen is a dead end with no next
      // action, and the point of this change is to route people to the truth, not strand them.
      fallbackStep: gates.fallbackScreen || "banking",
      reason:
        screen === "happyMoveIn"
          ? gates.mandate?.reason || "MANDATE_NOT_ACTIVE"
          : gates.banking?.reason || "BANKING_NOT_COMPLETE",
    };
  }

  return { decision: GATE_DECISION.ALLOW, reason: "ALLOWED" };
}
